import { useEffect } from 'react'
import { useAttachments } from '..'
import { XIcon } from '@heroicons/react/outline'
import { AttachmentName } from './AttachmentUtils'
import { useAttachmentUpload } from './useAttachmentUpload'
import { useTw } from '../../../../../../../../utils/Styles'
import { Attachment as AttachmentType } from '../../../../../../../../api/types'
import { useDeleteAttachmentMutation } from './DeleteAttachment.generated'
import { SmallAttachmentIcon } from '../../../../../../../../utils/AttachmentIcon'

const imgexts = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp']

type AttachmentProps = {
  name: string
  ext: string
  url?: string
}

function Attachment({ ext }: AttachmentProps) {
  const tw = useTw()
  return (
    <div className={tw`h-4 w-4`}>
      <SmallAttachmentIcon ext={ext} />
    </div>
  )
}

function UploadedAttachment({ attachment }: { attachment: AttachmentType }) {
  const tw = useTw()
  const { removeAttachment } = useAttachments()
  const [remove] = useDeleteAttachmentMutation()

  return (
    <div className={tw`flex justify-between px-5 py-3 items-center`}>
      <div className={tw`flex`}>
        <Attachment name={attachment.fileName} ext={attachment.extension} />
        <AttachmentName name={attachment.fileName} />
      </div>
      <div className={tw`rounded-full h-5 w-5`}>
        <div
          onClick={() =>
            remove({
              variables: { id: attachment._id },
            }).then(() => removeAttachment())
          }
          className={tw`z-10 hover:bg-neutral-400 cursor-pointer bg-neutral-350 rounded-full h-5 w-5`}
        >
          <XIcon className={tw`pt-1 ml-0.5 h-4 w-4 text-white`} />
        </div>
      </div>
    </div>
  )
}

function FileAttachment({ file }: { file: File }) {
  const tw = useTw()
  const ext = /(?:\.([^.]+))?$/.exec(file.name)[1] || ''
  const url = imgexts.includes(ext) ? URL.createObjectURL(file) : null
  return (
    <div className={tw`flex justify-between px-5 py-3 items-center`}>
      <div className={tw`flex`}>
        <Attachment ext={ext} url={url} name={file.name} />
        <AttachmentName name={file.name} />
      </div>
      <div className={tw`rounded-full h-5 w-5`}>
        <svg
          className={tw`animate-spin mt-0.5 ml-0.5 h-4 w-4 animate-spin text-foreground`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className={tw`opacity-25`}
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className={tw`opacity-75`}
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    </div>
  )
}

export function AttachmentHandler() {
  const { attachment, addAttachment } = useAttachments()
  const upload = useAttachmentUpload()
  useEffect(() => {
    if (attachment && 'lastModified' in attachment) {
      upload(attachment).then((att) => {
        addAttachment(att)
      })
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment])

  if (attachment && '_id' in attachment)
    return <UploadedAttachment attachment={attachment} />

  if (attachment && 'lastModified' in attachment)
    return <FileAttachment file={attachment} />

  return null
}

export function AttachmentItem() {
  const tw = useTw()
  const { attachment } = useAttachments()

  if (attachment)
    return (
      <div className={tw`border-t border-neutral-200`}>
        <AttachmentHandler />
      </div>
    )

  return null
}
